import { useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import { settableWorkerContractStatus, type SettableWorkerContractStatus, type WorkerContractStatus } from "@shyne/shared/api";

const schema = z.object({
  status: settableWorkerContractStatus,
});

type FormValues = z.infer<typeof schema>;

const preselected = (status: WorkerContractStatus): SettableWorkerContractStatus | undefined => settableWorkerContractStatus.safeParse(status).data;

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  currentStatus: WorkerContractStatus;
  onSubmit: (status: SettableWorkerContractStatus) => Promise<void>;
}

export default function EditIndividualStatusModal({ open, onOpenChange, currentStatus, onSubmit }: Props) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      status: preselected(currentStatus),
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        status: preselected(currentStatus),
      });
    }
  }, [open, currentStatus, form]);

  const fields: FormFieldDef[] = [
    {
      type: "select",
      name: "status",
      label: "Status",
      required: true,
      placeholder: "Choose a status",
      options: [
        { label: "Active", value: "active" },
        { label: "Suspended", value: "suspended" },
      ],
    },
  ];

  const handle = async (values: FormValues) => {
    await onSubmit(values.status);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Individual Status</DialogTitle>
        </DialogHeader>
        <FormCreator
          form={form}
          onSubmit={handle}
          formDefinition={fields}
          renderButtons={(statusForm) => (
            <Button className="w-full" type="submit" disabled={!statusForm.watch("status")}>
              Save
            </Button>
          )}
        />
      </DialogContent>
    </Dialog>
  );
}
